import React, { memo, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { MapPinIcon, XMarkIcon } from '@heroicons/react/24/solid'

import AutoCompleteAddress from '../../../components/AutoCompleteAddress'
import { SaveIcon } from '../../../components/elements/forms/FormDomain'
import { EmptyMessage } from '../../../components/EmptyMessage'
import { FormCardInput } from '../../../components/FormCard'
import WebsiteTab from '../../../components/WebsiteTab'
import { VALIDATION_URL_SM } from '../../../constants'
import { selectUserInfo } from '../../../features/users/userInfoSlice'
import { fetchWebsites, selectAll, updateWebsite } from '../../../features/websites/websitesSlice'

function ComSupports() {
  const dispatch = useDispatch();
  const userInfoContext = useSelector(selectUserInfo)

  /**
   * @type {Website[]}
   */
  const websites = useSelector(selectAll)

  /**
   * @type {Website}
   */
  const activeWebsite = useSelector(state => state.websites.activeWebsite)
  const [showItems, setShowItems] = useState(false)

  const {
    register: register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm(
    {
      shouldFocusError: true,
    }
  );

  const address = watch('address_gmb')

  const _showItems = useCallback(() => {
    setShowItems(true)
  }, [])

  const _hideItems = useCallback(() => {
    setTimeout(() => {
      setShowItems(false)
    }, 500)
  }, [])

  useEffect(() => {
    dispatch(fetchWebsites({ company_id: userInfoContext?.user?.company_id }))
  }, [userInfoContext?.user?.company_id])

  useEffect(() => {
    if (!activeWebsite) return
    setValue('name_gmb', activeWebsite?.name_gmb ?? document.querySelector('#name_gmb')?.value)
    setValue('address_gmb', activeWebsite?.address_gmb ?? document.querySelector('#address_gmb')?.value)
  }, [activeWebsite])

  const _submitData = useCallback((data) => {
    dispatch(updateWebsite({ site: { ...activeWebsite, ...data, website_id: activeWebsite?.id } }))
  }, [activeWebsite])

  const _resetForm = useCallback(() => {
    if (!activeWebsite?.name_gmb && !activeWebsite?.address_gmb) return
    dispatch(updateWebsite({ site: { ...activeWebsite, name_gmb: '', address_gmb: '', website_id: activeWebsite?.id } }))
  }, [activeWebsite])

  const messageByRole = !userInfoContext?.user?.admin ? "Veuillez contacter l'administrateur de votre companie pour ajouter un site." : 'Vérifiez que vous avez enregistré au moins un site pour avoir ajouter ses supports de communication.'
  return (
    <div className="max-w-4xl mx-auto space-y-3 mt-20">
      <div className="space-y-8">
        <div className="max-w-lg mx-auto mt-10">
          <WebsiteTab />
        </div>
      </div>
      {
        activeWebsite &&
        <p className="text-gray-400 text-xs text-center font-semibold pb-10">
          Merci de vérifier le nom ou l'URL de vos supports de communication <br /> <br />
          <span className="text-general-teal font-bold">Modifiez les si nécessaire</span>
        </p>
      }

      {activeWebsite && <>
        <form onSubmit={handleSubmit(_submitData)} className="flex gap-2 items-center justify-between bg-white p-3 rounded-2xl shadow">
          <FormCardInput
            placeholder='Ex: Azatys'
            type='text'
            label="Nom du Google my Business"
            name="name_gmb"
            rules={{ required: true, maxLength: 50 }}
            errorType={errors?.name_gmb?.type}
            register={register}
            icon={<GoogleMyBusinessIcon className="w-4 h-4 text-gray-500" />}
          />
          <div className='w-full relative'>
            <FormCardInput
              onFocus={_showItems}
              onBlur={_hideItems}
              placeholder='Ex:  24 Rue du Gouverneur Général Éboué, 92130 Issy-les-Moulineaux'
              type='text'
              label="Adresse du Google my Business"
              name="address_gmb"
              rules={{ required: false }}
              errorType={errors?.address_gmb?.type}
              register={register}
              icon={<MapPinIcon className="w-4 h-4 text-gray-500" />}
            />
            <AutoCompleteAddress name="address_gmb" setValue={setValue} query={address} showItems={showItems} />
          </div>
          <ActionsForm resetForm={_resetForm} />
        </form>

        <FormFacebook saveData={_submitData} />
        <FormInstagram saveData={_submitData} />
      </>
      }

      {websites.length <= 0 && <EmptyMessage title="Vous n'avez enregistré aucun site !" message={messageByRole} />}
      {!activeWebsite && websites.length > 0 && <EmptyMessage title='Selectionner un site !' message='Cliquez sur un site dans le menu au dessus pour ajouter ses supports de communication.' />}
    </div>
  )
}

export default memo(ComSupports)

/**
 * ACTIONS FORM
 * @param {{ resetForm: () => void }}
 * @returns {JSX.Element}
 */
export function ActionsForm({ resetForm }) {
  const loadingWebsite = useSelector(state => state.websites.loading)
  return <>
    <div className="tooltip" data-tip='Enregistrer'>
      <button type='submit' disabled={loadingWebsite} className="rounded-full text-2xl h-10 w-10 aspect-square justify-center border-offers-teal flex items-center border-2 -mb-4 disabled:cursor-not-allowed text-offers-teal disabled:text-gray-500 disabled:bg-gray-100 disabled:border-gray-300">
        <SaveIcon className="w-4" />
      </button>
    </div>
    <div className="tooltip" data-tip='Vider les champs'>
      <button type='reset' onClick={resetForm} disabled={loadingWebsite} className="rounded-full text-2xl h-10 w-10 aspect-square justify-center border-red-400 flex items-center border-2 -mb-4 disabled:cursor-not-allowed text-red-400 disabled:text-gray-500 disabled:bg-gray-100 disabled:border-gray-300">
        <XMarkIcon className="w-4" />
      </button>
    </div>
  </>
}
/**
 * FORM FECEBOOK COMPONENT
 * @param {{ saveData: () => void }} props 
 * @returns {JSX.Element}
 */
function FormFacebook({ saveData }) {
  /**
   * @type {Website}
   */
  const activeWebsite = useSelector(state => state.websites.activeWebsite)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm(
    {
      shouldFocusError: true,
    }
  );

  useEffect(() => {
    if (!activeWebsite) return
    if ('is_url_checked' in activeWebsite)
      setValue('url_facebook', activeWebsite?.is_url_checked ? activeWebsite?.url_facebook ?? document.querySelector('#url_facebook')?.value : 'SARA recherche ces informations, veuillez revenir plus tard.')
    else
      setValue('url_facebook', activeWebsite?.url_facebook ?? '')
  }, [activeWebsite])

  const _resetForm = useCallback(() => {
    if (!activeWebsite?.url_facebook) return
    dispatch(updateWebsite({ site: { ...activeWebsite, url_facebook: '', website_id: activeWebsite?.id } }))
  }, [activeWebsite])

  return <form onSubmit={handleSubmit(saveData)} className="flex gap-2 items-center justify-between bg-white p-3 rounded-2xl shadow">
    <FormCardInput
      disabled={'is_url_checked' in activeWebsite ? !activeWebsite?.is_url_checked : false}
      placeholder='Ex: https://www.facebook.com/sarabyazatys'
      type='url'
      label="URL Facebook"
      name="url_facebook"
      rules={{ required: true, pattern: VALIDATION_URL_SM }}
      errorType={errors?.url_facebook?.type}
      register={register}
      icon={<FacebookIcon className="w-4 h-4 text-gray-500" />}
    />
    <ActionsForm resetForm={_resetForm} />
  </form>
}

/**
 * FORM INSTAGRAM COMPONENT
 * @param {{ saveData: () => void }} props 
 * @returns {JSX.Element}
 */
function FormInstagram({ saveData }) {
  /**
   * @type {Website}
   */
  const activeWebsite = useSelector(state => state.websites.activeWebsite)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm(
    {
      shouldFocusError: true,
    }
  );

  useEffect(() => {
    if (!activeWebsite) return
    if ('is_url_checked' in activeWebsite)
      setValue('url_instagram', activeWebsite?.is_url_checked ? activeWebsite?.url_instagram ?? document.querySelector('#url_instagram')?.value : 'SARA recherche ces informations, veuillez revenir plus tard.')
    else
      setValue('url_instagram', activeWebsite?.url_instagram ?? '')
  }, [activeWebsite])

  const _resetForm = useCallback(() => {
    if (!activeWebsite?.url_instagram) return
    dispatch(updateWebsite({ site: { ...activeWebsite, url_instagram: '', website_id: activeWebsite?.id } }))
  }, [activeWebsite])

  return <form onSubmit={handleSubmit(saveData)} className="flex gap-2 items-center justify-between bg-white p-3 rounded-2xl shadow">
    <FormCardInput
      disabled={'is_url_checked' in activeWebsite ? !activeWebsite?.is_url_checked : false}
      placeholder='Ex: https://www.instagram.com/sarabyazatys'
      type='url'
      label="URL Instagram"
      name="url_instagram"
      rules={{ required: true, pattern: VALIDATION_URL_SM }}
      errorType={errors?.url_instagram?.type}
      register={register}
      icon={<InstagramIcon className="w-4 h-4 text-gray-500" />}
    />
    <ActionsForm resetForm={_resetForm} />
  </form>
}


export function GoogleMyBusinessIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}><path fill="currentColor" d="M22 8.5c0 1.37-1.12 2.5-2.5 2.5S17 9.87 17 8.5c0 1.37-1.12 2.5-2.5 2.5S12 9.87 12 8.5c0 1.37-1.12 2.5-2.5 2.5S7 9.87 7 8.5C7 9.87 5.88 11 4.5 11S2 9.87 2 8.5l1.39-5.42S3.68 2 4.7 2h14.6c1.02 0 1.31 1.08 1.31 1.08L22 8.5m-1 3.7V20c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2v-7.8a3.96 3.96 0 0 0 4-.58c.69.55 1.56.88 2.5.88c.95 0 1.82-.33 2.5-.88c.69.55 1.56.88 2.5.88c.95 0 1.82-.33 2.5-.88c.68.55 1.56.88 2.5.88c.53 0 1.04-.11 1.5-.3m-2 5.13c0-.2 0-.41-.05-.63l-.03-.16h-2.97v1.17h1.81c-.06.22-.14.44-.31.62c-.33.33-.78.51-1.26.51c-.5 0-.99-.21-1.35-.56c-.69-.71-.69-1.86.02-2.58c.69-.7 1.83-.7 2.55-.03l.14.13l.84-.85l-.16-.14c-.56-.52-1.3-.81-2.08-.81h-.01c-.81 0-1.57.31-2.14.87c-.59.58-.92 1.34-.92 2.13c0 .8.31 1.54.88 2.09a3.2 3.2 0 0 0 2.22.91h.02c.8 0 1.51-.29 2.03-.8c.47-.48.77-1.2.77-1.87Z"></path></svg>
  )
}


export function FacebookIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}><path fill="currentColor" d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"></path></svg>
  )
}


export function InstagramIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}><path fill="currentColor" d="M12 9.52A2.48 2.48 0 1 0 14.48 12A2.48 2.48 0 0 0 12 9.52Zm9.93-2.45a6.53 6.53 0 0 0-.42-2.26a4 4 0 0 0-2.32-2.32a6.53 6.53 0 0 0-2.26-.42C15.64 2 15.26 2 12 2s-3.64 0-4.93.07a6.53 6.53 0 0 0-2.26.42a4 4 0 0 0-2.32 2.32a6.53 6.53 0 0 0-.42 2.26C2 8.36 2 8.74 2 12s0 3.64.07 4.93a6.86 6.86 0 0 0 .42 2.27a3.94 3.94 0 0 0 .91 1.4a3.89 3.89 0 0 0 1.41.91a6.53 6.53 0 0 0 2.26.42C8.36 22 8.74 22 12 22s3.64 0 4.93-.07a6.53 6.53 0 0 0 2.26-.42a3.89 3.89 0 0 0 1.41-.91a3.94 3.94 0 0 0 .91-1.4a6.6 6.6 0 0 0 .42-2.27C22 15.64 22 15.26 22 12s0-3.64-.07-4.93Zm-2.54 8a5.73 5.73 0 0 1-.39 1.8A3.86 3.86 0 0 1 16.87 19a5.73 5.73 0 0 1-1.81.35H8.94A5.73 5.73 0 0 1 7.13 19a3.51 3.51 0 0 1-1.31-.86A3.51 3.51 0 0 1 5 16.87a5.49 5.49 0 0 1-.34-1.81V8.94A5.49 5.49 0 0 1 5 7.13a3.51 3.51 0 0 1 .86-1.31A3.59 3.59 0 0 1 7.13 5a5.73 5.73 0 0 1 1.81-.35h6.12a5.73 5.73 0 0 1 1.81.35a3.51 3.51 0 0 1 1.31.86A3.51 3.51 0 0 1 19 7.13a5.73 5.73 0 0 1 .35 1.81V12c0 2.06.07 2.27.04 3.06Zm-1.6-7.44a2.38 2.38 0 0 0-1.41-1.41A4 4 0 0 0 15 6H9a4 4 0 0 0-1.38.26a2.38 2.38 0 0 0-1.41 1.36A4.27 4.27 0 0 0 6 9v6a4.27 4.27 0 0 0 .26 1.38a2.38 2.38 0 0 0 1.41 1.41a4.27 4.27 0 0 0 1.33.26h6a4 4 0 0 0 1.38-.26a2.38 2.38 0 0 0 1.41-1.41a4 4 0 0 0 .26-1.38V9a3.78 3.78 0 0 0-.26-1.38ZM12 15.82A3.81 3.81 0 0 1 8.19 12A3.82 3.82 0 1 1 12 15.82Zm4-6.89a.9.9 0 0 1 0-1.79a.9.9 0 0 1 0 1.79Z"></path></svg>
  )
}